import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import type { MasterColumnsResult } from '../hooks/useMasterColumns';

interface MasterColumnChooserProps {
  visible: boolean;
  columns: MasterColumnsResult;
  onHide: () => void;
  /** An extra class on the dialog, for a screen styling its own (Purchase Requests' design21 look). */
  className?: string;
  /** The title; "Manage Columns" when absent. */
  heading?: string;
  /** A Done button beside Restore To Default that closes the dialog (Purchase Requests). */
  showDone?: boolean;
  /**
   * A Cancel button (Supplier, Purchase Requests and the PR / Supplier Onboarding templates, 2026-10-23): it closes the
   * dialog and puts the columns back as they were when it opened -- a change applies live, so Cancel undoes it.
   */
  showCancel?: boolean;
}

/** Gap between the label and the tooltip, and the least distance it keeps from the viewport edge. */
const TIP_GAP = 8;
const VIEWPORT_MARGIN = 8;

interface ColumnTip {
  text: string;
  /** The truncated label the tooltip names, in viewport coordinates. */
  anchor: DOMRect;
  /** The card it belongs to, so the tooltip can describe it to assistive technology. */
  key: string;
}

/** Whether the label's text is cut short by its ellipsis. */
function isTruncated(element: HTMLElement): boolean {
  return element.scrollWidth > element.clientWidth + 1;
}

/**
 * The full name of a truncated column, over the label it belongs to.
 *
 * A custom tooltip rather than PrimeReact's: that one was bound once by selector and did not reach the
 * cards (a locked card is a disabled button, which receives no mouse events, and the binding missed
 * cards rendered after it), so a long name stayed cut off with no way to read it.
 *
 * Portalled to `<body>` and positioned `fixed`, so the dialog's scrolling body cannot clip it. It opens
 * above the label, drops below when there is no room above, and is clamped inside the viewport on
 * both axes.
 */
function ColumnNameTooltip({ tip, id }: { tip: ColumnTip; id: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden', top: 0, left: 0 });
  const [placement, setPlacement] = useState<'top' | 'bottom'>('top');

  useLayoutEffect(() => {
    const element = ref.current;
    if (!element) return;
    const { width, height } = element.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const roomAbove = tip.anchor.top - TIP_GAP - VIEWPORT_MARGIN;
    const below = roomAbove < height;
    let top = below ? tip.anchor.bottom + TIP_GAP : tip.anchor.top - TIP_GAP - height;
    top = Math.max(VIEWPORT_MARGIN, Math.min(top, viewportHeight - height - VIEWPORT_MARGIN));

    const centre = tip.anchor.left + tip.anchor.width / 2;
    const left = Math.max(VIEWPORT_MARGIN, Math.min(centre - width / 2, viewportWidth - width - VIEWPORT_MARGIN));

    /* The arrow stays over the label even when the plate is pushed in from an edge. */
    const arrow = Math.max(12, Math.min(centre - left, width - 12));

    setPlacement(below ? 'bottom' : 'top');
    setStyle({ top, left, visibility: 'visible', ['--ms-tip-arrow' as string]: `${String(arrow)}px` });
  }, [tip]);

  return createPortal(
    <div ref={ref} id={id} role="tooltip" className={`ms-cols-tip ms-cols-tip--${placement}`} style={style}>
      {tip.text}
    </div>,
    document.body,
  );
}

/**
 * "Manage Columns" -- every available column as a card that toggles it.
 *
 * A centred dialog rather than a panel hanging off the toolbar button, which is
 * what the reference draws and what removes the last anchored popup from this
 * screen. The old panel was 272px of scrolling list pinned to the right-hand
 * end of the toolbar; Organization offers twenty-six columns, so it opened as a
 * tall column over the table and, near the right edge, over the status filter
 * beside it. The reference's two-column grid shows the same set without
 * overlapping anything.
 *
 * A change still applies immediately rather than on a Done button: the
 * reference shows the table reflowing behind the dialog, which is also what
 * makes the control explorable.
 */
export function MasterColumnChooser({
  visible,
  columns,
  onHide,
  className,
  heading = 'Manage Columns',
  showDone = false,
  showCancel = false,
}: MasterColumnChooserProps) {
  const tipId = useId();

  /* The columns shown when the dialog opened, for Cancel to return to -- taken once per opening. */
  const opened = useRef<ReadonlySet<string>>(new Set());
  const { available, isVisible } = columns;
  useEffect(() => {
    if (!visible) return;
    opened.current = new Set(available.filter((column) => isVisible(column.key)).map((column) => column.key));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a snapshot of the opening, not of every toggle after it
  }, [visible]);
  const cancel = () => {
    for (const column of columns.available) {
      if (columns.isLocked?.(column.key)) continue;
      if (columns.isVisible(column.key) !== opened.current.has(column.key)) columns.toggle(column.key);
    }
    onHide();
  };
  const [tip, setTip] = useState<ColumnTip | null>(null);
  const hideTip = useCallback(() => {
    setTip(null);
  }, []);

  /* Shown only for a name the ellipsis actually cut short; a name that fits needs no second copy. */
  const showTip = (key: string, text: string, card: HTMLElement) => {
    const label = card.querySelector<HTMLElement>('.ms-cols__label');
    if (!label || !isTruncated(label)) {
      setTip(null);
      return;
    }
    setTip({ key, text, anchor: label.getBoundingClientRect() });
  };

  /* A fixed tooltip would float away from its label once anything scrolls or resizes, so it closes instead. */
  useEffect(() => {
    if (!tip) return undefined;
    window.addEventListener('scroll', hideTip, true);
    window.addEventListener('resize', hideTip);
    return () => {
      window.removeEventListener('scroll', hideTip, true);
      window.removeEventListener('resize', hideTip);
    };
  }, [tip, hideTip]);

  /*
    Whether focus came from the keyboard. The dialog focuses its first card itself as it opens, and that
    focus used to open the tooltip unasked every time Manage Columns was shown. Focus opens it only once the
    user has pressed a key in the dialog; hover is unaffected.
  */
  const keyboard = useRef(false);

  useEffect(() => {
    keyboard.current = false;
    if (!visible) {
      setTip(null);
      return undefined;
    }
    const pressed = () => {
      keyboard.current = true;
    };
    window.addEventListener('keydown', pressed, true);
    return () => {
      window.removeEventListener('keydown', pressed, true);
    };
  }, [visible]);

  return (
    <Dialog
      /* Scoped backdrop: the mask is portalled to <body>, so no ancestor can reach it. */
      maskClassName="ms-mask"
      visible={visible}
      modal
      draggable={false}
      resizable={false}
      className={`ms-dialog ms-cols-dialog${className ? ` ${className}` : ''}`}
      onHide={onHide}
      header={
        <div className="ms-pop__head">
          <span className="ms-pop__icon" aria-hidden="true">
            <i className="pi pi-objects-column" />
          </span>
          <span className="ms-pop__title">{heading}</span>
        </div>
      }
      footer={
        <div className="ms-cols__footer">
          <Button
            type="button"
            outlined
            className="ms-pop__action"
            icon="pi pi-replay"
            label="Restore To Default"
            onClick={() => {
              columns.restoreDefaults();
            }}
          />
          {showCancel ? (
            <Button type="button" outlined className="ms-pop__action ms-cols__cancel" label="Cancel" onClick={cancel} />
          ) : null}
          {showDone ? (
            <Button type="button" className="ms-pop__action ms-pop__action--primary" label="Done" onClick={onHide} />
          ) : null}
        </div>
      }
    >
      <div className="ms-cols__grid">
        {columns.available.map((column) => {
          const on = columns.isVisible(column.key);
          /* Code and Name: ticked and inert, so the row always says which record it is. */
          const locked = columns.isLocked?.(column.key) ?? false;
          const described = tip?.key === column.key;

          return (
            <button
              key={column.key}
              type="button"
              role="checkbox"
              aria-checked={on}
              /*
                aria-disabled rather than disabled: a disabled button receives no mouse events, so a locked
                card's tooltip could never open. The click is refused below instead.
              */
              aria-disabled={locked || undefined}
              aria-describedby={described ? tipId : undefined}
              className={`ms-cols__card${on ? ' is-on' : ''}${locked ? ' is-locked' : ''}`}
              onClick={() => {
                if (locked) return;
                columns.toggle(column.key);
              }}
              onMouseEnter={(event) => {
                showTip(column.key, column.label, event.currentTarget);
              }}
              onMouseLeave={hideTip}
              onFocus={(event) => {
                if (keyboard.current) showTip(column.key, column.label, event.currentTarget);
              }}
              onBlur={hideTip}
            >
              <i className="pi pi-bars ms-cols__grip" aria-hidden="true" />
              <span className="ms-cols__box" aria-hidden="true">
                {on ? <i className="pi pi-check" /> : null}
              </span>
              <span className="ms-cols__label">{column.label}</span>
            </button>
          );
        })}
      </div>
      {visible && tip ? <ColumnNameTooltip tip={tip} id={tipId} /> : null}
    </Dialog>
  );
}
